import { setAlert, useAppDispatch, useAppSelector } from "@lib/stores";
import { ContentActions } from "@lib/stores/content/slices";
import * as Api from "@modules/api";
import { clearSelectedPlayerContent } from "@lib/stores";

import { db } from "@modules/local/db";
import { useAppLiveQuery } from "@modules/local/hooks";
import { detailsPageType } from "@utils/helpers/detailsPageType";
import { AlertType } from "@lib/stores/common/types";
import { translate } from "@modules/localizations/translate";
import { setStorage } from "@modules/storage/storageHelper";
import { useAppNavigate } from "@utils/hooks/useAppNavigate";

import { useAudioPlayer } from "../AudioPlayerContext";
import { useSelectedPlayerContent } from "../hooks/useSelectedPlayerContent";
import { SelectedPlayerContentSource } from "@lib/stores/content/types";

export const useContentSelector = () => {
  const navigate = useAppNavigate();

  const dispatch = useAppDispatch();

  const user = useAppSelector((state) => state.auth.user);

  const { result: flow } = useAppLiveQuery(
    () => db.readerFlows.get(user.id || ""),
    [user.id]
  );

  const {
    onPlayingChapterSelect,
    onFirstPlayingChapterSelect,
    onPlayerContentSelect,
  } = useSelectedPlayerContent();

  const { togglePlayer, changeIsAutoStart, load, unload } = useAudioPlayer();

  const onAudioSelect = (audioUrl: string | undefined) => {
    unload();

    if (audioUrl) {
      setTimeout(() => {
        load(audioUrl);
      }, 100);
    }
  };

  const onSelectContent = async (
    content: Api.Server.ContentEntityOutput,
    item: { [key: string]: string | string[] | null | undefined },
    config?: {
      forcedAutoPlayDisabled?: boolean;
      narratorId?: number;
      isLocked?: boolean;
      skipDetailsPageCheck?: boolean;
      source?: SelectedPlayerContentSource;
      nextItemSource?: Api.Server.NextItemSource;
      playlistId?: number;
    }
  ) => {
    const typeNavigation = flow?.typeNavigations?.find(
      (t) => t.contentEntityTypeId === content.contentEntityTypeId
    );

    if (
      (typeNavigation?.detailsPageId === typeNavigation?.playerPageId &&
        !detailsPageType(typeNavigation?.detailsPageType || "")) ||
      config?.skipDetailsPageCheck
    ) {
      if (config?.isLocked && !config.skipDetailsPageCheck) {
        setAlert({
          show: true,
          for: "page",
          type: AlertType.WARNING,
          messages: [
            {
              code: 0,
              field: null,
              message: translate(
                "content-details.messages.subscribe to listen"
              ),
            },
          ],
        });

        return;
      }

      //
      clearSelectedPlayerContent({ source: config?.source });

      togglePlayer(true);
      changeIsAutoStart(
        config?.forcedAutoPlayDisabled !== undefined
          ? !config?.forcedAutoPlayDisabled
          : config?.isLocked
            ? false
            : !user.autoPlayDisabled
      );

      const response = await dispatch(
        ContentActions.getContentEntitiesProgressAsync([content.id || ""])
      );

      if (response.meta.requestStatus === "fulfilled") {
        const data = response.payload as
          | Api.Server.ContentEntityProgressOutput[]
          | undefined;

        const progress = data?.[0];

        if (content?.id) {
          onPlayerContentSelect(
            {
              content: {
                id: content.id,
                title: content.title,
                author: content.authors?.join(", "),
                thumbnail: (item.image || item.thumbnail) as string,
                contentEntityType: content.contentEntityType,
                source: config?.source,
              },
              narratorId: config?.narratorId,
              isLocked: config?.isLocked,
              nextItemSource: config?.nextItemSource,
              playlistId: config?.playlistId,
            },
            (chapters) => {
              const chapterId = progress?.currentItem || chapters[0].id;

              const audioUrl = chapters.find(
                (c: any) => c.id === chapterId
              )?.signedAudioUrl;

              onPlayingChapterSelect(chapterId, () => {
                onAudioSelect(audioUrl);
              });

              onFirstPlayingChapterSelect(chapterId);
            }
          );
        }
      }
      //

      setStorage({
        key: "LATEST_PLAYING_CONTENT",
        data: {
          content,
          pageId: typeNavigation?.detailsPageId || "",
          item,
        },
      });
    } else {
      navigate(`/details/${content.id}/`, {
        nextItemSource:
          config?.nextItemSource !== undefined ? config.nextItemSource : "",
        playlistId: config?.playlistId || "",
      });
    }
  };

  return { onSelectContent };
};
